import { useCallback } from 'react';
import type { APIError, Conversation, Message } from '../../../api/types';
import { Chat } from '../../Chat';
import { ConversationList } from '../../shared/ConversationList';
import { ErrorDisplay, TypingIndicator } from '../../shared';
import styles from './FullChatLayout.module.css';
import { StreamingMessage } from '../../core';

export interface FullChatLayoutProps {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  streamingMessage: string;
  isSending: boolean;
  isLoading: boolean;
  error?: APIError | null;
  onSendMessage: (message: string) => void;
  onSelectConversation: (conversation: Conversation) => void;
  onNewConversation: () => void;
  onClearError?: () => void;
  showUsageGuidelines?: boolean;
  footerText?: string;
  className?: string;
}

export function FullChatLayout({
  conversations = [],
  currentConversation,
  messages,
  streamingMessage,
  isSending,
  isLoading,
  error,
  onSendMessage,
  onSelectConversation,
  onNewConversation,
  onClearError,
  showUsageGuidelines,
  footerText,
  className = '',
}: FullChatLayoutProps) {
  const handleSend = useCallback(
    (message: string) => {
      onSendMessage(message);
    },
    [onSendMessage]
  );

  return (
    <div className={`${styles.container} ${className}`}>
      <ConversationList
        conversations={conversations}
        currentConversation={currentConversation}
        onSelect={onSelectConversation}
        onNew={onNewConversation}
        isLoading={isLoading}
      />

      <Chat
        variant="full"
        conversationId={currentConversation?.conversation_id}
        messages={messages}
        streamingMessage={streamingMessage}
        isSending={isSending}
        isLoading={isLoading}
        error={error}
        currentConversation={currentConversation}
      >
        <Chat.Header
          title={currentConversation?.title || 'DevXGPT Assistant'}
          variant="default"
        />

        {error && <ErrorDisplay error={error} onDismiss={onClearError} />}

        <Chat.Messages
          messages={messages}
          isLoading={isLoading}
          variant="default"
        />
        
        {isSending && !streamingMessage && <TypingIndicator />}
        {streamingMessage && <StreamingMessage content={streamingMessage} />}

        <Chat.Input
          onSend={handleSend}
          disabled={isSending}
          isSending={isSending}
          variant="default"
        />
        <Chat.Footer
          showUsageGuidelines={showUsageGuidelines}
          footerText={footerText}
        />
      </Chat>
    </div>
  );
}
